import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  IconButton,
  Card,
  CardContent,
  Grid,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';

const InterestChangeRequests = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve', 'approve-modify', or 'reject'
  const [adminComments, setAdminComments] = useState('');
  const [modifiedInterestAmount, setModifiedInterestAmount] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/interest-change-requests/pending');
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching interest change requests:', error);
      showToast('Failed to load interest change requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setActionType('approve');
    setAdminComments('');
    setModifiedInterestAmount('');
    setActionDialogOpen(true);
  };

  const handleApproveWithModification = (request) => {
    setSelectedRequest(request);
    setActionType('approve-modify');
    setAdminComments('');
    setModifiedInterestAmount(request.requestedInterestAmount?.toString() || '0');
    setActionDialogOpen(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setActionType('reject');
    setAdminComments('');
    setModifiedInterestAmount('');
    setActionDialogOpen(true);
  };

  const submitAction = async () => {
    if (!selectedRequest) return;

    try {
      let endpoint;
      let params = {
        adminId: user.userId,
        adminComments: adminComments.trim() || undefined,
      };

      if (actionType === 'approve') {
        endpoint = `/admin/interest-change-requests/${selectedRequest.id}/approve`;
      } else if (actionType === 'approve-modify') {
        endpoint = `/admin/interest-change-requests/${selectedRequest.id}/approve-with-modification`;
        if (!modifiedInterestAmount || parseFloat(modifiedInterestAmount) < 0) {
          showToast('Please enter a valid modified interest amount', 'error');
          return;
        }
        params.modifiedInterestAmount = parseFloat(modifiedInterestAmount);
      } else {
        endpoint = `/admin/interest-change-requests/${selectedRequest.id}/reject`;
      }

      await api.post(endpoint, null, { params });
      
      const actionText = actionType === 'approve' 
        ? 'approved' 
        : actionType === 'approve-modify' 
        ? 'approved with modification' 
        : 'rejected';
      showToast(`Interest change request ${actionText} successfully`, 'success');
      
      setActionDialogOpen(false);
      setDetailDialogOpen(false);
      fetchRequests();
    } catch (error) {
      console.error('Error processing interest change request:', error);
      showToast(error.response?.data?.error || 'Failed to process request', 'error');
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return 'Rs. 0.00';
    return `Rs. ${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'COMPLETED':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Interest Change Requests
      </Typography>
      
      <Card sx={{ mt: 2 }}>
        <CardContent>
          {requests.length === 0 ? (
            <Alert severity="info">No pending interest change requests</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Request ID</strong></TableCell>
                    <TableCell><strong>Loan ID</strong></TableCell>
                    <TableCell><strong>Requested By</strong></TableCell>
                    <TableCell><strong>Request Type</strong></TableCell>
                    <TableCell align="right"><strong>Current Interest</strong></TableCell>
                    <TableCell align="right"><strong>Requested Interest</strong></TableCell>
                    <TableCell><strong>Requested At</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id} hover>
                      <TableCell><strong>#{request.id}</strong></TableCell>
                      <TableCell><strong>{request.loan?.id || 'N/A'}</strong></TableCell>
                      <TableCell>
                        {request.requestedBy?.fullName || request.requestedBy?.username || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={request.requestType} 
                          color={request.requestType === 'REMOVE' ? 'error' : 'primary'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(request.currentInterestAmount)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        {request.requestType === 'REMOVE' ? (
                          <Chip label="Remove (0)" color="error" size="small" />
                        ) : (
                          <strong>{formatCurrency(request.requestedInterestAmount)}</strong>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(request.requestedAt)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={request.status} 
                          color={getStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(request)}
                            title="View Details"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(request)}
                            title="Approve"
                          >
                            <CheckCircleIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleApproveWithModification(request)}
                            title="Approve with Modification"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleReject(request)}
                            title="Reject"
                          >
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Interest Change Request Details
          <IconButton
            aria-label="close"
            onClick={() => setDetailDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Request ID</Typography>
                  <Typography variant="body1"><strong>#{selectedRequest.id}</strong></Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={selectedRequest.status} 
                    color={getStatusColor(selectedRequest.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Loan ID</Typography>
                  <Typography variant="body1"><strong>{selectedRequest.loan?.id || 'N/A'}</strong></Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Request Type</Typography>
                  <Chip 
                    label={selectedRequest.requestType} 
                    color={selectedRequest.requestType === 'REMOVE' ? 'error' : 'primary'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Requested By</Typography>
                  <Typography variant="body1">
                    {selectedRequest.requestedBy?.fullName || selectedRequest.requestedBy?.username || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Requested At</Typography>
                  <Typography variant="body1">{formatDate(selectedRequest.requestedAt)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Current Total Interest</Typography>
                  <Typography variant="h6" color="error">
                    {formatCurrency(selectedRequest.currentInterestAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRequest.requestType === 'REMOVE' ? 'Requested Interest' : 'Requested Interest Amount'}
                  </Typography>
                  {selectedRequest.requestType === 'REMOVE' ? (
                    <Typography variant="h6" color="error">
                      Remove (Set to 0)
                    </Typography>
                  ) : (
                    <Typography variant="h6" color="primary">
                      {formatCurrency(selectedRequest.requestedInterestAmount)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Reason</Typography>
                  <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                    <Typography variant="body1">
                      {selectedRequest.reason || 'No reason provided'}
                    </Typography>
                  </Paper>
                </Grid>
                {selectedRequest.adminComments && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Admin Comments</Typography>
                    <Paper sx={{ p: 2, mt: 1, bgcolor: 'info.light', color: 'white' }}>
                      <Typography variant="body1">
                        {selectedRequest.adminComments}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {selectedRequest.reviewedBy && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Reviewed By</Typography>
                    <Typography variant="body1">
                      {selectedRequest.reviewedBy?.fullName || selectedRequest.reviewedBy?.username || 'Unknown'}
                    </Typography>
                  </Grid>
                )}
                {selectedRequest.reviewedAt && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Reviewed At</Typography>
                    <Typography variant="body1">{formatDate(selectedRequest.reviewedAt)}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          {selectedRequest && selectedRequest.status === 'PENDING' && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleApprove(selectedRequest);
                }}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="info"
                startIcon={<EditIcon />}
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleApproveWithModification(selectedRequest);
                }}
              >
                Approve with Modification
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleReject(selectedRequest);
                }}
              >
                Reject
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog 
        open={actionDialogOpen} 
        onClose={() => setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === 'approve' && 'Approve Interest Change Request'}
          {actionType === 'approve-modify' && 'Approve with Modification'}
          {actionType === 'reject' && 'Reject Interest Change Request'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedRequest && (
              <>
                <Alert severity={actionType === 'reject' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                  {actionType === 'approve' && (
                    <>This will apply the requested interest change to the installments.</>
                  )}
                  {actionType === 'approve-modify' && (
                    <>You can modify the requested interest amount before approving.</>
                  )}
                  {actionType === 'reject' && (
                    <>This will reject the interest change request. The request cannot be undone.</>
                  )}
                </Alert>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Current Interest</Typography>
                    <Typography variant="h6">{formatCurrency(selectedRequest.currentInterestAmount)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Requested Interest</Typography>
                    <Typography variant="h6">
                      {selectedRequest.requestType === 'REMOVE' 
                        ? 'Remove (0)' 
                        : formatCurrency(selectedRequest.requestedInterestAmount)}
                    </Typography>
                  </Grid>
                </Grid>

                {actionType === 'approve-modify' && (
                  <TextField
                    fullWidth
                    label="Modified Interest Amount"
                    type="number"
                    value={modifiedInterestAmount}
                    onChange={(e) => setModifiedInterestAmount(e.target.value)}
                    sx={{ mb: 2 }}
                    helperText={`Original requested: ${formatCurrency(selectedRequest.requestedInterestAmount || 0)}`}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                )}

                <TextField
                  fullWidth
                  label="Admin Comments"
                  multiline
                  rows={4}
                  value={adminComments}
                  onChange={(e) => setAdminComments(e.target.value)}
                  helperText="Optional comments for this action"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={actionType === 'reject' ? 'error' : 'primary'}
            onClick={submitAction}
            disabled={actionType === 'approve-modify' && (!modifiedInterestAmount || parseFloat(modifiedInterestAmount) < 0)}
          >
            {actionType === 'approve' && 'Approve'}
            {actionType === 'approve-modify' && 'Approve with Modification'}
            {actionType === 'reject' && 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterestChangeRequests;
